import React, { memo } from "react"

/**
 * The technology stack section, rebuilt into the service-page system.
 *
 * KEPT, NOT DROPPED. The instruction was explicit that this is real content
 * buyers read, so every icon group and all three prose paragraphs survive. What
 * changed is where it sits (after section 4 rather than near the end), the voice
 * (second person), and the styling, which is now .sp- rather than the old
 * .tech-stack rules in services.scss.
 *
 * WHY IT IS NOT JUST <WEB_TechStack /> MOVED. Three reasons, all measured:
 *
 *  1. It rendered <Title> with no level, and antd's Typography.Title defaults to
 *     h1. Moving it in unchanged would have put a second h1 on a page whose h1
 *     is frozen. Headings here are h2/h3.
 *
 *  2. Its ground is #0D1B67 and section 4's is $deep, #0C1754. Two bands that
 *     close together, touching, read as one long dark stretch — which is what
 *     the adjacent-ground rule exists to prevent. This sits on $paper instead,
 *     between the dark band above and the white section below.
 *
 *  3. The old markup is styled by .tech-stack rules that services.scss shares
 *     with the mobile page. Restyling in place would have moved a page that is
 *     not in scope yet.
 *
 * The old WEB_TechStack.js is left untouched on disk and is now unreferenced.
 *
 * `groups` is [{ label, items: [{ node, name, chip }] }] — `node` is the icon
 * element, `name` is the text label. Both are rendered: the name is what makes a
 * term present in the copy rather than present as a picture, which is the
 * difference between a reader and a search engine seeing it.
 *
 * `chip` puts the icon on a dark tile. The AI logos are PNGs drawn as pure white
 * glyphs on transparency — measured, mean luminance 255 — because the section
 * they came from sat on a dark ground. On $paper they disappear completely. A
 * chip restores the ground they were drawn for, and works whether the glyph is
 * white or coloured, which inverting them would not.
 */

const ServiceTechStack = ({ eyebrow, heading, lede, groups, paragraphs }) => (
  <section className="sp-stack">
    <div className="sp-stack__inner">
      <span className="sp-tag">{eyebrow}</span>
      <h2 className="sp-h2">{heading}</h2>
      {lede && <p className="sp-lede">{lede}</p>}

      <div className="sp-stack__cols">
        <div className="sp-stack__groups">
          {groups.map(g => (
            <div className="sp-stack__group" key={g.label}>
              <h3 className="sp-stack__grouplabel">{g.label}</h3>
              <ul className="sp-stack__items">
                {g.items.map(it => (
                  <li className="sp-stack__item" key={it.name}>
                    <span
                      className={
                        "sp-stack__icon" +
                        (it.chip ? " sp-stack__icon--chip" : "")
                      }
                      aria-hidden="true"
                    >
                      {it.node}
                    </span>
                    <span className="sp-stack__name">{it.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="sp-stack__prose">
          {paragraphs.map((p, i) => (
            <p className="sp-stack__para" key={i}>
              {p}
            </p>
          ))}
        </div>
      </div>
    </div>
  </section>
)

export default memo(ServiceTechStack)
